import { AdvancedFilterType } from 'talent_matching/types';
import type { SavedSearch } from 'talent_search/shared/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { extractFormFieldsFromSavedSearch as extractTalentSearchFormFields } from 'talent_search/lib/query_params';

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function toSavedSearchQueryString(
  params: Record<string, unknown>
): string {
  const searchParams = new URLSearchParams();

  for (const paramKey in params) {
    if (params.hasOwnProperty(paramKey)) {
      const key = paramKey;
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((element) => {
          if (typeof element === 'object') {
            const serializedElement = JSON.stringify(element);
            searchParams.append(`${String(key)}[]`, serializedElement);
          } else {
            searchParams.append(`${String(key)}[]`, element);
          }
        });
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          searchParams.append(`${String(key)}[${subKey}]`, subValue as string);
        });
      } else {
        searchParams.append(key as string, value as string);
      }
    }
  }

  return searchParams.toString();
}

function parseFormFieldsFromSavedSearch(
  params: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const key in params) {
    if (!params.hasOwnProperty(key)) {
      continue;
    }

    const value = params[key];

    if (Array.isArray(value)) {
      result[key] = value.map((element) => {
        if (typeof element === 'string') {
          return safeJsonParse(element);
        }
        return element;
      });
    } else if (typeof value === 'object' && value !== null) {
      result[key] = value;
    } else if (value === 'null') {
      result[key] = null;
    } else if (value === 'true') {
      result[key] = true;
    } else if (value === 'false') {
      result[key] = false;
    } else {
      result[key] = value;
    }
  }

  return result;
}

const ADVANCED_FILTER_KEYS = [
  'advancedFilters',
  'questionFilters',
  'educationFilters',
] as const;

function hydrateAdvancedFilters(
  parsed: Record<string, unknown>
): Record<string, unknown> {
  for (const key of ADVANCED_FILTER_KEYS) {
    if (Array.isArray(parsed[key])) {
      parsed[key] = (parsed[key] as unknown[]).map((value) => {
        if (value instanceof AdvancedFilter) {
          return value;
        }
        if (isRecord(value)) {
          return new AdvancedFilter(value as AdvancedFilterType);
        }
        return new AdvancedFilter();
      });
    }
  }

  return parsed;
}

export function extractFormFieldsFromSavedSearch(
  params: SavedSearch
): SavedSearch {
  const parsed = parseFormFieldsFromSavedSearch(
    params as unknown as Record<string, unknown>
  );

  return hydrateAdvancedFilters(parsed) as unknown as SavedSearch;
}

// Past candidates keeps the talent_search parser (keywords stay raw strings,
// custom fields become FieldObjects) but still needs AdvancedFilter instances.
export function extractRediscoveryFormFieldsFromSavedSearch(
  params: SavedSearch,
  userDateFormat: string
): SavedSearch {
  const parsed = extractTalentSearchFormFields(params, userDateFormat);

  return hydrateAdvancedFilters(
    parsed as unknown as Record<string, unknown>
  ) as unknown as SavedSearch;
}
